import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Wrench, ExternalLink, Clock, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api, resolveMediaUrl } from "@/lib/api";
import { getPlatformMeta } from "@/lib/socialPlatforms";

/**
 * MaintenancePage
 * Shown to public visitors while the site is in maintenance mode.
 * Includes: brand logo, big heading, custom message, optional ETA, social/support cards,
 * and a discreet admin login link.
 */
export default function MaintenancePage({ settings }) {
    const [social, setSocial] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/social");
                setSocial(Array.isArray(data) ? data : []);
            } catch {
                /* ignore - the page still works without social links */
            }
        })();
    }, []);

    const title = (settings?.maintenance_title || "Prace serwisowe").trim();
    const message = (settings?.maintenance_message || "").trim();
    const eta = (settings?.maintenance_eta || "").trim();
    const logoUrl = settings?.logo_url || "";
    const seoTitle = "Project Ruda Śląska";

    const socialLinks = social.filter((l) => l.category === "social" || !l.category);
    const contactLinks = social.filter((l) => l.category === "kontakt");
    const supportLinks = social.filter((l) => l.category === "support");

    return (
        <div
            data-testid="maintenance-page"
            className="relative min-h-screen bg-[color:var(--bg-base)] text-white overflow-hidden"
        >
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="robots" content="noindex,nofollow" />
                <meta httpEquiv="refresh" content="60" />
            </Helmet>

            {/* Background glow */}
            <div className="pointer-events-none absolute inset-0 z-0 opacity-70">
                <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-[140px] bg-[color:var(--primary)]/15" />
                <div className="absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full blur-[160px] bg-[#22c55e]/10" />
            </div>

            <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-16 md:py-24 flex flex-col min-h-screen">
                {/* Top brand row */}
                <div className="flex items-center justify-between gap-4 mb-16 md:mb-20">
                    <div className="flex items-center gap-3 min-w-0">
                        {logoUrl ? (
                            <span className="inline-flex items-center justify-center h-10 max-w-[220px] overflow-hidden shrink-0">
                                <img
                                    src={resolveMediaUrl(logoUrl)}
                                    alt="Project Ruda Śląska"
                                    className="max-h-full max-w-full object-contain"
                                />
                            </span>
                        ) : (
                            <span className="w-10 h-10 flex items-center justify-center rounded-sm border border-[color:var(--border-strong)] text-[color:var(--primary)] font-display font-bold shrink-0">
                                PR
                            </span>
                        )}
                        <span className="hidden sm:flex flex-col leading-tight min-w-0">
                            <span className="font-display font-semibold uppercase tracking-wider text-sm text-white truncate">
                                Project Ruda Śląska
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-secondary)]">
                                ETS2 · Mapa 1:1
                            </span>
                        </span>
                    </div>
                    <Link
                        to="/admin/login"
                        data-testid="maintenance-admin-link"
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-muted)] hover:text-[color:var(--primary)] transition-colors"
                    >
                        <ShieldCheck size={12} strokeWidth={1.8} />
                        <span className="hidden sm:inline">Panel admina</span>
                    </Link>
                </div>

                {/* Main content */}
                <div className="flex-1 flex flex-col justify-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/10 text-[color:var(--primary)] text-[10px] uppercase tracking-[0.3em] mb-6">
                            <Wrench size={12} strokeWidth={2} />
                            Status: tryb serwisowy
                        </div>

                        <h1
                            data-testid="maintenance-title"
                            className="font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-white mb-6"
                        >
                            {title}
                        </h1>

                        {message && (
                            <p
                                data-testid="maintenance-message"
                                className="text-base md:text-lg text-[color:var(--text-secondary)] max-w-2xl whitespace-pre-line leading-relaxed"
                            >
                                {message}
                            </p>
                        )}

                        {eta && (
                            <div
                                data-testid="maintenance-eta"
                                className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[color:var(--text-secondary)]"
                            >
                                <Clock size={14} className="text-[color:var(--primary)]" />
                                <span>
                                    Przewidywany powrót:{" "}
                                    <span className="text-white font-medium normal-case tracking-normal">
                                        {eta}
                                    </span>
                                </span>
                            </div>
                        )}
                    </motion.div>

                    {/* Social & Support */}
                    {(socialLinks.length > 0 || supportLinks.length > 0 || contactLinks.length > 0) && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
                            className="mt-14 md:mt-16 space-y-8"
                            data-testid="maintenance-social"
                        >
                            {socialLinks.length > 0 && (
                                <MaintenanceGroup title="Śledź projekt" links={socialLinks} testidPrefix="maint-social" />
                            )}
                            {contactLinks.length > 0 && (
                                <MaintenanceGroup title="Kontakt" links={contactLinks} testidPrefix="maint-contact" />
                            )}
                            {supportLinks.length > 0 && (
                                <MaintenanceGroup
                                    title="Wesprzyj projekt"
                                    links={supportLinks}
                                    testidPrefix="maint-support"
                                    accent
                                />
                            )}
                        </motion.div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.28em] text-[color:var(--text-muted)]">
                    <span>
                        © {new Date().getFullYear()} Project Ruda Śląska
                    </span>
                    <span>ETS2 · Modification · v{settings?.version || "-"}</span>
                </div>
            </div>
        </div>
    );
}

function MaintenanceGroup({ title, links, testidPrefix, accent = false }) {
    return (
        <div>
            <div
                className={`text-[10px] uppercase tracking-[0.3em] mb-4 ${
                    accent ? "text-[color:var(--primary)]" : "text-[color:var(--text-secondary)]"
                }`}
            >
                {title}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {links.map((link) => (
                    <MaintenanceLinkCard key={link.id} link={link} testid={`${testidPrefix}-${link.id}`} />
                ))}
            </div>
        </div>
    );
}

function MaintenanceLinkCard({ link, testid }) {
    const meta = getPlatformMeta(link.platform);
    const Icon = meta.Icon;
    const href =
        link.url.startsWith("mailto:") || /^https?:\/\//.test(link.url)
            ? link.url
            : link.platform === "email"
              ? `mailto:${link.url}`
              : `https://${link.url}`;
    return (
        <a
            href={href}
            data-testid={testid}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-3.5 rounded-lg border border-white/5 bg-[color:var(--bg-surface)]/60 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5"
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${meta.color}55`;
                e.currentTarget.style.boxShadow = `0 6px 24px ${meta.color}22`;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <div
                className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                style={{ background: `${meta.color}1a`, color: meta.color }}
            >
                <Icon size={18} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                    {meta.label}
                </div>
                <div className="text-sm text-white truncate">
                    {link.label || meta.label}
                </div>
            </div>
            <ExternalLink size={14} className="text-[color:var(--text-muted)] group-hover:text-white shrink-0" />
        </a>
    );
}
